import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import type { NavItem } from '../shared/types/index.js';

/**
 * Sub-navigatie (tabbalk) van een module. Items met `menu: 'meer'` staan onder "Meer ▾".
 * Het menu is `position: fixed`, zodat de horizontaal scrollbare tabbalk (mobiel) het niet afknipt.
 */
export function ModuleSubNav({ items }: { items: NavItem[] }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const meerKnop = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const label = (item: NavItem) => (item.labelKey ? t(item.labelKey, { defaultValue: item.label }) : item.label);
  const hoofd = items.filter(i => i.menu !== 'meer');
  const meer = items.filter(i => i.menu === 'meer');
  const actiefInMeer = meer.find(i => location.pathname === i.path);

  // Menu sluiten bij navigatie, klik buiten het menu, scroll of resize.
  useEffect(() => { setMenuPos(null); }, [location.pathname]);
  useEffect(() => {
    if (!menuPos) return;
    const sluit = (e: Event) => {
      const doel = e.target as Node;
      if (menuRef.current?.contains(doel) || meerKnop.current?.contains(doel)) return;
      setMenuPos(null);
    };
    const sluitAltijd = () => setMenuPos(null);
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuPos(null); };
    document.addEventListener('mousedown', sluit);
    document.addEventListener('touchstart', sluit);
    document.addEventListener('keydown', esc);
    window.addEventListener('resize', sluitAltijd);
    window.addEventListener('scroll', sluitAltijd, true);
    return () => {
      document.removeEventListener('mousedown', sluit);
      document.removeEventListener('touchstart', sluit);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('resize', sluitAltijd);
      window.removeEventListener('scroll', sluitAltijd, true);
    };
  }, [menuPos]);

  const toggleMenu = () => {
    if (menuPos) { setMenuPos(null); return; }
    const r = meerKnop.current?.getBoundingClientRect();
    if (!r) return;
    const breedte = 220;
    setMenuPos({ top: r.bottom + 4, left: Math.max(8, Math.min(r.left, window.innerWidth - breedte - 8)) });
  };

  const knop = (item: NavItem) => {
    const isActive = location.pathname === item.path;
    return (
      <button
        key={item.path}
        onClick={() => navigate(item.path)}
        aria-current={isActive ? 'page' : undefined}
        className={clsx(
          'px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap flex-shrink-0',
          isActive ? 'bg-gold/10 text-gold' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
        )}
      >
        <span className="mr-1.5">{item.icon}</span>
        {label(item)}
        {item.badge !== undefined && item.badge > 0 && (
          <span className="ml-1.5 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{item.badge}</span>
        )}
      </button>
    );
  };

  return (
    <div className="h-10 bg-white border-b border-gray-200 flex items-center px-4 gap-1 flex-shrink-0 overflow-x-auto">
      {hoofd.map(knop)}
      {meer.length > 0 && (
        <>
          <button
            ref={meerKnop}
            onClick={toggleMenu}
            aria-haspopup="menu"
            aria-expanded={!!menuPos}
            className={clsx(
              'px-3 py-1.5 text-xs font-medium rounded-md transition-colors whitespace-nowrap flex-shrink-0 inline-flex items-center gap-1',
              actiefInMeer || menuPos ? 'bg-gold/10 text-gold' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
            )}
          >
            {actiefInMeer ? <><span>{actiefInMeer.icon}</span>{label(actiefInMeer)}</> : t('common.more', { defaultValue: 'Meer' })}
            <ChevronDown size={12} />
          </button>
          {menuPos && (
            <div
              ref={menuRef}
              role="menu"
              className="fixed z-50 w-[220px] bg-white border border-gray-200 rounded-lg shadow-lg py-1"
              style={{ top: menuPos.top, left: menuPos.left }}
            >
              {meer.map(item => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    role="menuitem"
                    onClick={() => navigate(item.path)}
                    className={clsx(
                      'w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors',
                      isActive ? 'bg-gold/10 text-gold font-medium' : 'text-gray-700 hover:bg-gray-50'
                    )}
                  >
                    <span>{item.icon}</span>{label(item)}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
